import React, { useCallback, useEffect, useRef, useState } from "react";
import { Formik, Form, Field, FastField } from "formik";
import {
  Box,
  Button,
  Grid,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import {
  createFinishedGood,
  getFinishedGoodById,
  getFinishedGoodImage,
  patchFinishedGood,
  uploadFinishedGoodImage,
} from "../../api/finishedGoods";
import { getCatalogue } from "../../api/common/catalogues.ts";
import { getCurrencyList } from "../../api/common/currencies";
import DimensionBoxPreview from "./DimensionBoxPreview";
import { palletDimsFromProps } from "./palletProps";

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const NUMERIC_FIELDS = [
  "itemHeight",
  "itemWidth",
  "itemDepth",
  "itemWeight",
  "unitPrice",
  "masterHeight",
  "masterWidth",
  "masterDepth",
  "masterNetWeight",
  "masterGrossWeight",
  "palletHeight",
  "mastersPerLayer",
  "layersPerPallet",
];

const OPTIONAL_ID_FIELDS = [
  "tariffFractionId",
  "hsCodeId",
  "satCodeId",
  "palletId",
  "currencyId",
];

const initialFormData = {
  name: "",
  code: "",
  description: "",
  imgFile: null,
  tariffFractionId: "",
  hsCodeId: "",
  satCodeId: "",
  itemHeight: 0,
  itemWidth: 0,
  itemDepth: 0,
  itemWeight: 0,
  currencyId: 1,
  unitPrice: 0,
  masterHeight: 0,
  masterWidth: 0,
  masterDepth: 0,
  masterNetWeight: 0,
  masterGrossWeight: 0,
  palletId: "",
  palletHeight: 0,
  mastersPerLayer: 0,
  layersPerPallet: 0,
};

const toOptionalId = (value) =>
  value === "" || value == null ? null : Number(value);

const coercePatchValue = (field, raw) => {
  if (OPTIONAL_ID_FIELDS.includes(field)) {
    return toOptionalId(raw);
  }
  if (NUMERIC_FIELDS.includes(field)) {
    if (raw === "" || raw == null) return 0;
    return Number(raw);
  }
  if (field === "description") {
    return raw || null;
  }
  return raw;
};

function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`fg-tabpanel-${index}`}
      aria-labelledby={`fg-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const numberField = (name, label, size = 4, onBlurField) => (
  <Grid item xs={12} sm={size}>
    <Field name={name}>
      {({ field }) => (
        <TextField
          {...field}
          fullWidth
          type="number"
          label={label}
          inputProps={{ step: "any", min: 0 }}
          onBlur={(e) => {
            field.onBlur(e);
            onBlurField?.(name, e);
          }}
        />
      )}
    </Field>
  </Grid>
);

const textField = (name, label, size = 12, extra = {}, onBlurField) => (
  <Grid item xs={12} sm={size}>
    <Field name={name}>
      {({ field }) => (
        <TextField
          {...field}
          fullWidth
          label={label}
          {...extra}
          onBlur={(e) => {
            field.onBlur(e);
            onBlurField?.(name, e);
          }}
        />
      )}
    </Field>
  </Grid>
);

export default function FinishedGoodForm({ onSubmit, id, companyId }) {
  const [initialValues, setInitialValues] = useState(initialFormData);
  const [loading, setLoading] = useState(Boolean(id));
  const [tab, setTab] = useState(0);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [palletPreviewDims, setPalletPreviewDims] = useState(null);
  const fileInputRef = useRef(null);
  const previewObjectUrlRef = useRef(null);
  const { openSnack } = useSnack();

  const isEdit = Boolean(id);

  const clearPreviewObjectUrl = useCallback(() => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  }, []);

  const loadImagePreview = useCallback(
    async (finishedGoodId, hasImg) => {
      clearPreviewObjectUrl();
      setImagePreviewUrl(null);
      if (!finishedGoodId || !hasImg) return;
      try {
        const res = await getFinishedGoodImage(finishedGoodId);
        const url = URL.createObjectURL(res.data);
        previewObjectUrlRef.current = url;
        setImagePreviewUrl(url);
      } catch {
        // No image or failed — leave empty
      }
    },
    [clearPreviewObjectUrl]
  );

  useEffect(() => {
    return () => clearPreviewObjectUrl();
  }, [clearPreviewObjectUrl]);

  useEffect(() => {
    if (!id) {
      setInitialValues(initialFormData);
      setPalletPreviewDims(null);
      clearPreviewObjectUrl();
      setImagePreviewUrl(null);
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const res = await getFinishedGoodById(id);
        const data = res.data || {};
        setInitialValues({
          ...initialFormData,
          ...data,
          tariffFractionId: data.tariffFractionId ?? "",
          hsCodeId: data.hsCodeId ?? "",
          satCodeId: data.satCodeId ?? "",
          palletId: data.palletId ?? "",
          currencyId: data.currencyId ?? data.currency?.id ?? 1,
          description: data.description ?? "",
        });
        const dims = palletDimsFromProps(data.pallet?.props ?? data.pallet?.Props);
        setPalletPreviewDims(dims);
        await loadImagePreview(id, Boolean(data.imgFile));
      } catch (e) {
        openSnack(handleError(e), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, openSnack, loadImagePreview, clearPreviewObjectUrl]);

  const patchField = useCallback(
    async (field, rawValue) => {
      if (!id) return;
      const value = coercePatchValue(field, rawValue);
      try {
        await patchFinishedGood(id, { [field]: value });
      } catch (e) {
        openSnack(handleError(e), "error");
      }
    },
    [id, openSnack]
  );

  const handleBlurField = useCallback(
    (fieldName, event) => {
      // Let Formik finish its blur first
      const value = event?.target?.value;
      if (!isEdit) return;
      patchField(fieldName, value);
    },
    [isEdit, patchField]
  );

  const handleDropdownChange = useCallback(
    (fieldName) => (event, option) => {
      if (fieldName === "palletId") {
        setPalletPreviewDims(
          palletDimsFromProps(option?.props ?? option?.Props)
        );
      }
      if (!isEdit) return;
      patchField(fieldName, event?.target?.value);
    },
    [isEdit, patchField]
  );

  const handleImageSelect = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !id) return;

    if (file.size > MAX_IMAGE_BYTES) {
      openSnack("La imagen no debe superar 5MB", "error");
      return;
    }
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      openSnack("Solo se permiten imágenes JPEG o PNG", "error");
      return;
    }

    setUploadingImage(true);
    try {
      // Local preview immediately
      clearPreviewObjectUrl();
      const localUrl = URL.createObjectURL(file);
      previewObjectUrlRef.current = localUrl;
      setImagePreviewUrl(localUrl);

      await uploadFinishedGoodImage(id, file);
      openSnack("Imagen actualizada", "success");
    } catch (e) {
      openSnack(handleError(e), "error");
      await loadImagePreview(id, true);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreate = async (values, { setSubmitting }) => {
    const payload = {
      ...values,
      companyId,
      description: values.description || null,
      imgFile: undefined,
    };

    delete payload.piecesPerMaster;
    delete payload.piecesPerBed;
    delete payload.piecesPerPallet;
    delete payload.itemVolume;
    delete payload.masterVolume;
    delete payload.pallet;
    delete payload.currency;
    delete payload.company;
    delete payload.tariffFraction;
    delete payload.hsCode;
    delete payload.satCode;
    delete payload.imgFile;

    NUMERIC_FIELDS.forEach((field) => {
      if (payload[field] === "" || payload[field] == null) {
        payload[field] = 0;
      } else {
        payload[field] = Number(payload[field]);
      }
    });

    OPTIONAL_ID_FIELDS.forEach((field) => {
      payload[field] = toOptionalId(payload[field]);
    });

    try {
      await createFinishedGood(payload);
      openSnack("Producto terminado creado con éxito", "success");
      onSubmit();
    } catch (e) {
      openSnack(handleError(e), "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return null;
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleCreate}
    >
      {({ values, isSubmitting }) => (
        <Box
          component={Form}
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: { xs: 300, md: 860 },
            maxWidth: 960,
            p: 1,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Datos Generales" id="fg-tab-0" />
            <Tab label="Componentes" id="fg-tab-1" />
            <Tab label="Dimensiones" id="fg-tab-2" />
            <Tab label="Datos adicionales" id="fg-tab-3" />
          </Tabs>

          {/* Tab 1 — Datos Generales */}
          <TabPanel value={tab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  {textField("name", "Nombre Producto", 12, { required: true }, handleBlurField)}
                  {textField("code", "Clave", 12, { required: true }, handleBlurField)}
                  {textField(
                    "description",
                    "Descripción",
                    12,
                    { multiline: true, rows: 5 },
                    handleBlurField
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  onClick={() => {
                    if (isEdit && !uploadingImage) fileInputRef.current?.click();
                  }}
                  sx={{
                    height: 220,
                    borderRadius: 1,
                    bgcolor: "warning.light",
                    border: "2px dashed",
                    borderColor: "warning.main",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: isEdit ? "pointer" : "not-allowed",
                    opacity: isEdit ? 1 : 0.7,
                    overflow: "hidden",
                    position: "relative",
                    mb: 2,
                    "&:hover": isEdit
                      ? { bgcolor: "warning.main", "& .upload-hint": { opacity: 1 } }
                      : {},
                  }}
                >
                  {imagePreviewUrl ? (
                    <Box
                      component="img"
                      src={imagePreviewUrl}
                      alt="Producto"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        bgcolor: "#fff",
                      }}
                    />
                  ) : (
                    <>
                      <CloudUploadIcon sx={{ fontSize: 40, color: "warning.dark", mb: 1 }} />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Imagen
                      </Typography>
                      <Typography variant="caption" color="text.secondary" align="center" px={2}>
                        {isEdit
                          ? "Clic para subir (JPEG/PNG, máx. 5MB)"
                          : "Disponible después de crear el producto"}
                      </Typography>
                    </>
                  )}
                  {uploadingImage && (
                    <Typography
                      variant="caption"
                      sx={{ position: "absolute", bottom: 8 }}
                    >
                      Subiendo…
                    </Typography>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    hidden
                    onChange={handleImageSelect}
                  />
                </Box>

                <Grid container spacing={2}>
                  {numberField("unitPrice", "Precio unitario", 6, handleBlurField)}
                  <Grid item xs={12} sm={6}>
                    <Field
                      name="currencyId"
                      label="Moneda"
                      as={DropdownSelector}
                      fetchFunction={getCurrencyList}
                      optionLabel="symbol"
                      onChange={handleDropdownChange("currencyId")}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {!isEdit && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !values.name || !values.code}
                >
                  Crear
                </Button>
              </Box>
            )}
          </TabPanel>

          {/* Tab 2 — Componentes */}
          <TabPanel value={tab} index={1}>
            <Box
              sx={{
                minHeight: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                EN CONSTRUCCIÓN
              </Typography>
            </Box>
          </TabPanel>

          {/* Tab 3 — Dimensiones */}
          <TabPanel value={tab} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Producto
                </Typography>
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  {numberField("itemHeight", "Alto (cm)", 4, handleBlurField)}
                  {numberField("itemDepth", "Largo (cm)", 4, handleBlurField)}
                  {numberField("itemWidth", "Ancho (cm)", 4, handleBlurField)}
                  {numberField("itemWeight", "Peso (kg)", 4, handleBlurField)}
                </Grid>

                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Master
                </Typography>
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  {numberField("masterHeight", "Alto (cm)", 4, handleBlurField)}
                  {numberField("masterDepth", "Largo (cm)", 4, handleBlurField)}
                  {numberField("masterWidth", "Ancho (cm)", 4, handleBlurField)}
                  {numberField("masterNetWeight", "Peso neto (kg)", 6, handleBlurField)}
                  {numberField("masterGrossWeight", "Peso bruto (kg)", 6, handleBlurField)}
                </Grid>

                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Pallet
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <FastField
                      name="palletId"
                      label="Tarima"
                      as={DropdownSelector}
                      fetchFunction={getCatalogue}
                      fetchParams={{
                        catalogueName: "PALLET",
                        pageSize: 1000,
                        orderBy: "code",
                      }}
                      optionLabel={["code", "name"]}
                      optionSeparator=" | "
                      onChange={handleDropdownChange("palletId")}
                    />
                  </Grid>
                  {numberField("palletHeight", "Alto (cm)", 6, handleBlurField)}
                  {numberField("mastersPerLayer", "Master / cama", 6, handleBlurField)}
                  {numberField("layersPerPallet", "Camas / tarima", 6, handleBlurField)}
                </Grid>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <DimensionBoxPreview
                    label="Producto"
                    width={values.itemWidth}
                    height={values.itemHeight}
                    depth={values.itemDepth}
                    color={0x5b8def}
                  />
                  <DimensionBoxPreview
                    label="Master"
                    width={values.masterWidth}
                    height={values.masterHeight}
                    depth={values.masterDepth}
                    color={0x43a047}
                  />
                  <DimensionBoxPreview
                    label="Pallet"
                    width={palletPreviewDims?.palletWidth || 0}
                    height={values.palletHeight}
                    depth={palletPreviewDims?.palletDepth || 0}
                    color={0xe8c96a}
                    baseHeight={
                      palletPreviewDims?.palletHeight &&
                      Number(values.palletHeight) >
                        palletPreviewDims.palletHeight
                        ? palletPreviewDims.palletHeight
                        : 0
                    }
                    baseColor={0xc47a2c}
                  />
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Tab 4 — Datos adicionales */}
          <TabPanel value={tab} index={3}>
            <Grid container spacing={2} sx={{ maxWidth: 480 }}>
              <Grid item xs={12}>
                <FastField
                  name="tariffFractionId"
                  label="Fracción Arancelaria"
                  as={DropdownSelector}
                  fetchFunction={getCatalogue}
                  fetchParams={{
                    catalogueName: "TARIFF_FRACTION",
                    pageSize: 1000,
                    orderBy: "code",
                  }}
                  optionLabel={["code", "name"]}
                  optionSeparator=" | "
                  onChange={handleDropdownChange("tariffFractionId")}
                />
              </Grid>
              <Grid item xs={12}>
                <FastField
                  name="hsCodeId"
                  label="HS Code"
                  as={DropdownSelector}
                  fetchFunction={getCatalogue}
                  fetchParams={{
                    catalogueName: "US_HS_CODE",
                    pageSize: 1000,
                    orderBy: "code",
                  }}
                  optionLabel={["code", "name"]}
                  optionSeparator=" | "
                  onChange={handleDropdownChange("hsCodeId")}
                />
              </Grid>
              <Grid item xs={12}>
                <FastField
                  name="satCodeId"
                  label="Clave SAT"
                  as={DropdownSelector}
                  fetchFunction={getCatalogue}
                  fetchParams={{
                    catalogueName: "CLAVES_SAT",
                    pageSize: 1000,
                    orderBy: "code",
                  }}
                  optionLabel={["code", "name"]}
                  optionSeparator=" | "
                  onChange={handleDropdownChange("satCodeId")}
                />
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
      )}
    </Formik>
  );
}
